import { createContext, useContext, useState, useEffect } from "react";
import dayjs from "dayjs";

import environment from "../env";
import { useAuth } from "./AuthProvider";
import useSWRImmutable from "swr/immutable";

import { useAccount } from "./AccountProvider";
import { useSfs } from "./SfsProvider";

const ArrangementContext = createContext();

/**
 * Arrangement custom hook
 * */
export const useArrangement = () => useContext(ArrangementContext);

const ArrangementProvider = ({ children }) => {

  const { accessToken } = useAuth();

  const { useGetAccount } = useAccount();
  const { account } = useGetAccount();
  const { disposableIncome } = useSfs();

  const [hasArrangement, setHasArrangement] = useState(null);
  const [plan, setPlan] = useState(null);
  const [lastSavedPlan, setLastSavedPlan] = useState(null);
  const [planMode, setPlanMode] = useState(null);
  const [planLoaded, setPlanLoaded] = useState(false);

  const apiKey = environment.CUSTOMER_PORTAL_API_KEY;
  const apiUrl = environment.CUSTOMER_PORTAL_API;

  const MAX_INSTALMENTS = 250;
  const PAYMENT_BUFFER_DAYS = 7;

  useEffect(() => {
    if (accessToken === null || accessToken === '') {
      // Reset state when access revoked
      setHasArrangement(null);
      setPlan(null);
      setLastSavedPlan(null);
      setPlanMode(null);
      setPlanLoaded(false);
    }
  }, [accessToken]);

  const defaultOptions = {
    method: "GET",
    headers: {
      "x-api-key": apiKey,
      Authorization: `Bearer ${accessToken}`,
    },
  };

  /**
   * Post arrangement callback
   * @param {*} url
   * @param {*} arg
   * @returns
   */
  async function postArrangement(url, arrangement) {
    let requestOptions = {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(arrangement),
    };
    console.info("post arrangement request: ", { url, requestOptions });
    try {
      const response = await fetch(url, requestOptions);
      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Arrangement creation failed:", response.status, errorText);
        return;
      }

      const data = await response.json();
      console.log("post arrangement response: ", data);
    } catch (error) {
      console.error(`Error in post arrangement: ${error}`);
    }
  }

  /**
   * Post propose arrangement callback
   * @param {*} url
   * @param {*} arg
   * @returns
   */
  async function postProposeArrangement(url, { arg }) {
    let requestOptions = {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(arg),
    };
    console.info("post propose arrangement request: ", { url, requestOptions });
    return fetch(`${apiUrl}${url}`, requestOptions).then((res) => res.json());
  }

  /**
   * Get arrangement SWR hook
   * @returns
   */
  function useGetArrangement() {
    const url = accessToken ? `${apiUrl}/arrangement` : null;
    console.info("Get arrangement request", { defaultOptions, url });
    const { data, error, isLoading } = useSWRImmutable([url, defaultOptions]);
    console.info("Get arrangement response", { data, error, isLoading });
    return {
      arrangement: data,
      error,
      isArrangementLoading: isLoading,
    };
  }

  /**
   * Get arrangement history SWR hook
   * @returns 
   */
  function useGetArrangementHistory() {
    const url = accessToken ? `${apiUrl}/arrangement/history` : null;
    console.info("Get arrangement history", { defaultOptions, url })
    const { data, error, isLoading } = useSWRImmutable([url, defaultOptions])
    console.info("Get arrangement history response", { data, error, isLoading })
    return {
      arrangementHistory: data,
      error,
      isArrangementHistoryLoading: isLoading,
    }
  }

  const paymentMethods = {
    "CASH PAYMENT": 1,
    "CHECK": 2,
    "MONEY ORDER": 3,
    "WESTERN UNION": 4,
    "DEBIT CARD PAYMENT": 5,
    "PAPER DRAFT": 6,
    "ACH DEBIT": 7,
    "POST-DATED CHECK": 8,
    "MERCHANDISE RETURN": 9,
    "RESERVED": 10,
    "UK DIRECT DEBIT": 11,
    "STANDING ORDER": 12,
    "DEBT MANAGEMENT": 13,
    "DIRECT TO BANK": 14,
    "CHEQUE PAYMENT": 15,
    "DIRECT TO CLIENT": 16,
    "PAYPOINT (BARCODE)": 17,
    "GOOGLE PAY": 18,
    "APPLE PAY": 19,
    "CHAPS": 20,
    "CASH": 21,
    "DEBIT CARD": 22,
    "UNSPECIFIED": 23,
    "OTHER": 24,
    "DEBIT DEBIT MANDATE": 25,
    "CLIENT CONTROLLED DD": 26,
    "SEPA": 28,
    "PAYPAL": 29,
    "DEBT ARR SCHEME": 30,
    "POSTAL ORDER": 31,
    "PAYMENT BOOK": 32,
  };

  // Define payment start date buffers in days
  const paymentBuffers = {
    "DEBIT CARD PAYMENT": 0,
    "UK DIRECT DEBIT": 10,
    "STANDING ORDER": 5,
    "DIRECT TO BANK": 5,
    "CHEQUE PAYMENT": 10,
    "POSTAL ORDER": 10,
  }

  const paymentFrequencies = {
    monthly: "Monthly",
    weekly: "Weekly",
    biweekly: "Fortnightly",
    every28days: "4 Weekly",
    monthlyPick: "Monthly Pick",
  };

  const frequencyEnums = {
    weekly: 1,
    biweekly: 2,
    monthly: 3,
    every28days: 4,
    monthlyPick: 5,
  };

  const ordinalEnums = {
    "First": 1,
    "Second": 2,
    "Third": 3,
    "Fourth": 4,
    "Last": 5,
  };

  const dayEnums = {
    "Sunday": 0,
    "Monday": 1,
    "Tuesday": 2,
    "Wednesday": 3,
    "Thursday": 4,
    "Friday": 5,
    "Saturday": 6,
    "Workday": 10,
  };

  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  /**
   * Calculate arrangement start date on load and when frequency
   * or method changes
   * @returns
   */
  const calculateStartDate = (
    paymentMethod,
    paymentFrequency,
    monthlyDay,
    weeklyDay
  ) => {
    // Get start date buffer for payment selected method
    const buffer = paymentBuffers[Object.keys(paymentMethods).find(
      key => paymentMethods[key] === paymentMethod)];

    // Start date cannot be before buffered date so start there
    let newStartDate = dayjs().add(buffer, "day");

    if (paymentFrequency && monthlyDay && weeklyDay) {
      // Now depending on frequency add any required further days
      if (paymentFrequency === "monthly") {
        // Move to next available selected day eg Day 1
        while (newStartDate.date() !== Number(monthlyDay)) {
          newStartDate = newStartDate.add("1", "day");
        }
      } else {
        // Move to next available day of week eg Tuesday
        while (daysOfWeek[newStartDate.day()] !== weeklyDay) {
          newStartDate = newStartDate.add("1", "day");
        }
      }
    }

    return newStartDate;
  };

  /**
   * Calculate arrangement end date on load and when frequency
   * or method changes
   * @returns
   */
  const calculateEndDate = (startDate, paymentFrequency, numInstalments) => {
    // Recalculate end date using new start date
    let newEndDate = null;
    // First payment is made on start date, so reduce required payment periods by 1 instalment
    const paymentPeriods = numInstalments - 1;
    switch (paymentFrequency) {
      case "monthly":
      case "monthlyPick":
      case "weekly":
        newEndDate = startDate.add(paymentPeriods, frequencyConversion[paymentFrequency]);
        break;
      case "biweekly":
        newEndDate = startDate.add(Math.ceil(paymentPeriods * 2), "weeks");
        break;
      case "every28days":
        newEndDate = startDate.add(Math.ceil(paymentPeriods * 4), "weeks");
        break;
      default:
        break;
    };

    return newEndDate;
  };

  /**
   * Calculate final payment amount
   * @returns
   */
  const getFinalPaymentAmount = (balance, paymentAmount, numInstalments) => {

    let finalPaymentAmount = null;

    // Calculate if all arguments have value
    if (balance && paymentAmount && numInstalments) {
      const totalRepaymentAmount = paymentAmount * numInstalments;
      if (totalRepaymentAmount > balance) {
        const overpaymentAmount = totalRepaymentAmount - balance;
        finalPaymentAmount = paymentAmount - overpaymentAmount;
      } else {
        finalPaymentAmount = paymentAmount;
      }
    }

    return finalPaymentAmount;
  }

  const checkHasArrangement = (arrangement) => {
    return arrangement && arrangement !== "{}" && Object.keys(arrangement).length > 0;
  };

  const getPaymentFrequencyInfo = (hasArrangement, arrangement) => {
    const currentDay = dayjs().date();
    const paymentDay = Math.min(currentDay, 28);

    let paymentFrequency = hasArrangement ? arrangement?.paymentFrequency : "monthlyPick";
    // Normalize frequency case - API might return different cases
    if (paymentFrequency === "monthlypick" || paymentFrequency === "MonthlyPick") {
      paymentFrequency = "monthlyPick";
    }

    const paymentFrequencyArgs = hasArrangement && arrangement?.paymentFrequencyArgs
      ? arrangement.paymentFrequencyArgs.split(",")[0] : paymentDay;

    const isMonthlyFrequency = paymentFrequency === "monthly" || paymentFrequency === "monthlyPick";
    const paymentMonthlyDay = isMonthlyFrequency ? paymentFrequencyArgs : paymentDay;
    const paymentWeeklyDay = isMonthlyFrequency ? "Monday" : paymentFrequencyArgs;

    return { paymentFrequency, paymentMonthlyDay, paymentWeeklyDay };
  };

  const calculatePaymentAmounts = (hasArrangement, arrangement, paymentFrequency) => {
    if (hasArrangement) {
      return {
        paymentAmount: arrangement?.instalmentAmount,
        numInstalments: arrangement?.numberOfInstalments,
        totalPayable: arrangement?.totalPayable
      };
    }

    // Ensure account balance is available
    if (!account?.currentBalance || account.currentBalance <= 0) {
      console.warn("Account balance not available for payment calculations");
      return {
        paymentAmount: 0,
        numInstalments: 1,
        totalPayable: 0
      };
    }

    if (paymentFrequency === "monthlyPick") {
      const defaultInstalments = 2;
      return {
        paymentAmount: account.currentBalance / defaultInstalments,
        numInstalments: defaultInstalments,
        totalPayable: account.currentBalance
      };
    }

    // Ensure disposable income is available for other frequencies
    if (!disposableIncome || disposableIncome <= 0) {
      console.warn("Disposable income not available for payment calculations");
      return {
        paymentAmount: account.currentBalance,
        numInstalments: 1,
        totalPayable: account.currentBalance
      };
    }

    const paymentAmount = Math.min(disposableIncome, account.currentBalance);
    const defaultNumInstalments = Number.parseInt(Math.ceil(account.currentBalance / disposableIncome));

    if (defaultNumInstalments <= MAX_INSTALMENTS) {
      return {
        paymentAmount,
        numInstalments: defaultNumInstalments,
        totalPayable: account.currentBalance
      };
    }

    return {
      paymentAmount,
      numInstalments: MAX_INSTALMENTS,
      totalPayable: MAX_INSTALMENTS * paymentAmount
    };
  };

  const calculatePaymentWindow = (hasArrangement, paymentMethod, nextPaymentDate) => {
    if (!hasArrangement) return false;

    const isDirectDebit = paymentMethod === paymentMethods["UK DIRECT DEBIT"];
    const diff = Math.ceil(dayjs(nextPaymentDate).diff(dayjs(), "day", true));

    return isDirectDebit && diff <= PAYMENT_BUFFER_DAYS;
  };

  const getSettlementInfo = (hasArrangement, arrangement, arrangementHistory) => {
    if (!hasArrangement) {
      return { type: null, settlementPercentage: null, discount: null, status: null, isSettlement: false };
    }

    const arrangementHistoryItem = arrangementHistory?.arrangementHistory?.find(
      item => item?.arrangementId === arrangement?.arrangementId && item?.status === "Active"
    );

    if (!arrangementHistoryItem) {
      return { type: null, settlementPercentage: null, discount: null, status: null, isSettlement: false };
    }

    const settlementPercentage = arrangementHistoryItem?.settlementPercentage < 1
      ? arrangementHistoryItem.settlementPercentage * 100
      : null;

    return {
      type: arrangementHistoryItem.type,
      settlementPercentage,
      discount: arrangementHistoryItem.discount,
      status: arrangementHistoryItem.status,
      isSettlement: Boolean(settlementPercentage)
    };
  };

  const determinePlanMode = (hasArrangement, isSettlement) => {
    if (!hasArrangement) return "create";
    return isSettlement ? "settle" : "amend";
  };

  /**
   * Co-ordinate load of external arrangement data into state
   * @returns
   */
  const loadPlanData = (arrangement, arrangementHistory) => {
    console.log("Load Arrangement Data: ", arrangement);
    console.log("Account data: ", account);
    console.log("Disposable Income: ", disposableIncome);

    try {
      const hasArrangement = checkHasArrangement(arrangement);
      console.log("Has arrangement: ", hasArrangement);

      const { paymentFrequency, paymentMonthlyDay, paymentWeeklyDay } = getPaymentFrequencyInfo(hasArrangement, arrangement);
      console.log("Payment frequency info: ", { paymentFrequency, paymentMonthlyDay, paymentWeeklyDay });

      const { paymentAmount, numInstalments, totalPayable } = calculatePaymentAmounts(hasArrangement, arrangement, paymentFrequency);
      console.log("Payment amounts: ", { paymentAmount, numInstalments, totalPayable });

      const paymentMethod = hasArrangement ? paymentMethods[arrangement?.paymentMethod] : paymentMethods["UK DIRECT DEBIT"];
      const startDate = hasArrangement ? dayjs(arrangement?.dateCreated) :
        calculateStartDate(paymentMethod, paymentFrequency, paymentMonthlyDay, paymentWeeklyDay);

      const endDate = hasArrangement || paymentFrequency === "monthlyPick" ? null :
        calculateEndDate(startDate, paymentFrequency, numInstalments);

      const nextPaymentDate = hasArrangement ? arrangement?.nextPaymentDate : startDate;
      const inPaymentWindow = calculatePaymentWindow(hasArrangement, paymentMethod, nextPaymentDate);
      const { type, settlementPercentage, discount, status, isSettlement } = getSettlementInfo(hasArrangement, arrangement, arrangementHistory);

      // Parse monthlyPick details from paymentFrequencyArgs
      let monthlyPickOrdinal = null;
      let monthlyPickDay = null;
      console.log("Parsing monthlyPick - frequency:", paymentFrequency, "hasArrangement:", hasArrangement, "paymentFrequencyArgs:", arrangement?.paymentFrequencyArgs);

      if (paymentFrequency === "monthlyPick" && hasArrangement && arrangement?.paymentFrequencyArgs) {
        const args = arrangement.paymentFrequencyArgs.split(",");
        monthlyPickOrdinal = args[0]?.trim() || "Third";
        monthlyPickDay = args[1]?.trim() || "Wednesday";
        console.log("Parsed monthlyPick values:", { monthlyPickOrdinal, monthlyPickDay, rawArgs: arrangement.paymentFrequencyArgs });
      } else if (paymentFrequency === "monthlyPick" && hasArrangement) {
        // If monthlyPick but no paymentFrequencyArgs, check individual fields
        monthlyPickOrdinal = arrangement?.monthlyPickOrdinal || "Third";
        monthlyPickDay = arrangement?.monthlyPickDay || "Wednesday";
        console.log("Using individual monthlyPick fields:", { monthlyPickOrdinal, monthlyPickDay });
      } else if (paymentFrequency === "monthlyPick") {
        monthlyPickOrdinal = "Third";
        monthlyPickDay = "Wednesday";
        console.log("Using default monthlyPick values:", { monthlyPickOrdinal, monthlyPickDay });
      }

      const planData = {
        dateCreated: dayjs(startDate),
        instalmentAmount: paymentAmount,
        nextPaymentAmount: hasArrangement ? arrangement?.nextPaymentAmount : null,
        nextPaymentDate: nextPaymentDate,
        inPaymentWindow: inPaymentWindow,
        numberOfInstalments: numInstalments,
        paymentsLeft: hasArrangement ? arrangement?.paymentsLeft : numInstalments,
        paidToDate: hasArrangement ? arrangement?.paidToDate : 0,
        paymentFrequency: paymentFrequency,
        paymentMethod: paymentMethod,
        totalPayable: totalPayable,
        totalRemaining: hasArrangement ? arrangement?.totalRemaining : totalPayable,
        sortCode: hasArrangement ? arrangement?.sortCode : null,
        accountNumber: hasArrangement ? arrangement?.accountNumber : null,
        walletId: hasArrangement ? arrangement?.walletId : null,
        isPromiseToPay: true,
        endDate: endDate ? dayjs(endDate) : null,
        weeklyDay: paymentWeeklyDay,
        monthlyPickOrdinal: monthlyPickOrdinal,
        monthlyPickDay: monthlyPickDay,
        monthlyDay: paymentMonthlyDay,
        finalPaymentAmount: paymentFrequency === "monthlyPick" ? null : getFinalPaymentAmount(account?.currentBalance, paymentAmount, numInstalments),
        type: type,
        settlementPercentage: settlementPercentage,
        discount: discount,
        status: status,
        isSettlement: isSettlement,
        sif: false,
      };

      console.log("PLAN DATA: ", planData);
      setPlan(planData);
      setLastSavedPlan(planData);
      setPlanMode(determinePlanMode(hasArrangement, isSettlement));
      setHasArrangement(hasArrangement);
      setPlanLoaded(true);
    } catch (error) {
      console.error("Error loading plan data: ", error);
      setPlanLoaded(true); // Set to true to prevent infinite loading
    }
  };

  useEffect(() => {
    if (account && plan && disposableIncome && disposableIncome >= 1) {
      // On DI change, update the default plan used by create arrangement feature
      if (hasArrangement === false && plan?.paymentFrequency !== "monthlyPick") {
        const currentDate = dayjs();
        const paymentAmount = disposableIncome <= account?.currentBalance ? disposableIncome : account?.currentBalance;
        const defaultNumInstalments = Number(Math.ceil(account?.currentBalance / disposableIncome));
        let numInstalments;
        let totalPayable;
        if (defaultNumInstalments <= MAX_INSTALMENTS) {
          numInstalments = defaultNumInstalments;
          totalPayable = account?.currentBalance;
        } else {
          numInstalments = MAX_INSTALMENTS;
          totalPayable = MAX_INSTALMENTS * paymentAmount;
        }
        const newEndDate = calculateEndDate(currentDate, plan?.paymentFrequency, numInstalments);
        setPlan({
          ...plan,
          instalmentAmount: paymentAmount,
          numberOfInstalments: numInstalments,
          totalPayable: totalPayable,
          totalRemaining: totalPayable,
          endDate: newEndDate,
          finalPaymentAmount: getFinalPaymentAmount(account?.currentBalance, paymentAmount, numInstalments)
        });
      }
    }
  }, [disposableIncome, account?.currentBalance, hasArrangement, plan?.paymentFrequency]);

  const getMonthlyDay = (plan) => {
    // monthlyDay should be current day for monthlyPick, or selected day for monthly
    if (plan?.paymentFrequency === "monthlyPick") {
      return dayjs().date();
    }
    if (plan?.paymentFrequency === "monthly") {
      return plan?.monthlyDay;
    }

    return null;
  };

  /**
   * Create wallet for monthlyPick direct debit
   */
  const createWallet = async (plan) => {
    const walletObj = {
      accountNumber: plan?.accountNumber,
      bankSort: plan?.sortCode,
      partyId: account?.partyId || 18
    };

    console.log("Creating wallet with:", walletObj);

    const requestOptions = {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(walletObj),
    };

    try {
      const response = await fetch(`${apiUrl}/wallet`, requestOptions);
      console.log("Wallet response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Wallet creation failed:", response.status, errorText);
        return null;
      }

      const data = await response.json();
      console.log("Wallet created successfully:", data);
      return data.walletId;
    } catch (error) {
      console.error("Error creating wallet:", error);
      return null;
    }
  };

  /**
   * Co-ordinate dump of internal arrangement data into POST body
   * @returns
   */
  const savePlanData = (plan) => {
    console.log("save plan data: ", plan);

    const arrangementObj = {
      accountId: account?.accountId,
      debtorId: account?.debtorId,
      startDate: plan?.dateCreated ? dayjs(plan.dateCreated).format("YYYY-MM-DD") : null,
      paymentAmount: plan?.instalmentAmount,
      totalAmount: plan?.totalPayable,
      paymentMethod: plan?.paymentMethod,
      frequency: plan?.paymentFrequency === "monthlyPick" ? "monthlypick" : plan?.paymentFrequency,
      walletItemId: plan?.paymentFrequency === "monthlyPick" ? 548024 : plan?.walletId,
      bankAccountNumber: plan?.accountNumber,
      sortCode: plan?.sortCode,
      SIF: plan?.sif,
      monthlyDay: plan?.paymentFrequency === "monthlyPick" ? dayjs().date() : (plan?.paymentFrequency === "monthly" ? plan?.monthlyDay : undefined),
      monthlyPickOrdinal: plan?.paymentFrequency === "monthlyPick" ? plan?.monthlyPickOrdinal : undefined,
      monthlyPickDay: plan?.paymentFrequency === "monthlyPick" ? plan?.monthlyPickDay : undefined,
      isPromiseToPay: true,
    };

    // Remove undefined fields
    Object.keys(arrangementObj).forEach(key => {
      if (arrangementObj[key] === undefined) {
        delete arrangementObj[key];
      }
    });

    console.log("=== FINAL ARRANGEMENT PAYLOAD ===");
    console.log(JSON.stringify(arrangementObj, null, 2));
    console.log("================================");

    if (accessToken) {
      postArrangement(`${apiUrl}/arrangement`, arrangementObj);
    }

    setLastSavedPlan(plan);
  };

  const frequencyConversion = {
    monthly: "months",
    monthlyPick: "months",
    weekly: "weeks",
    biweekly: "weeks",
    every28days: "weeks",
  };

  const updatePlanByPaymentAmount = (amount) => {

    // For monthlyPick, allow any amount (don't restrict to DI)
    if (plan?.paymentFrequency !== "monthlyPick" && amount > disposableIncome) {
      return;
    }

    // For monthlyPick, calculate instalments based on amount
    if (plan?.paymentFrequency === "monthlyPick") {
      const totalInstalments = Number(Math.ceil(account?.currentBalance / amount));
      const finalPaymentAmount = getFinalPaymentAmount(account?.currentBalance, amount, totalInstalments);

      setPlan({
        ...plan,
        instalmentAmount: amount,
        numberOfInstalments: totalInstalments,
        paymentsLeft: totalInstalments,
        totalPayable: account?.currentBalance,
        totalRemaining: account?.currentBalance,
        paidToDate: 0,
        finalPaymentAmount: finalPaymentAmount,
      });
      return;
    }

    // How many instalments would clear the balance using the new amount?
    const totalInstalments = Number(Math.ceil(account?.currentBalance / amount));

    let planNumInstalments = null;
    let finalPaymentAmount = null;
    let planTotalAmount = null;

    if (totalInstalments <= MAX_INSTALMENTS) {
      // This amount can clear the balance without exceeding max allowed instalments
      planNumInstalments = totalInstalments;
      finalPaymentAmount = getFinalPaymentAmount(account?.currentBalance, amount, planNumInstalments);
      planTotalAmount = account?.currentBalance;
    } else {
      // This amount cannot clear the balance so cap at max instalments
      planNumInstalments = MAX_INSTALMENTS;
      finalPaymentAmount = amount;
      planTotalAmount = amount * planNumInstalments;
    }

    // Recalculate end date using new instalment number
    setPlan({
      ...plan,
      instalmentAmount: amount,
      numberOfInstalments: planNumInstalments,
      paymentsLeft: planNumInstalments,
      totalPayable: planTotalAmount,
      totalRemaining: planTotalAmount,
      paidToDate: 0,
      finalPaymentAmount: finalPaymentAmount,
    });
  };

  const contextValue = {
    useGetArrangement,
    postArrangement,
    postProposeArrangement,
    useGetArrangementHistory,
    loadPlanData,
    savePlanData,
    planLoaded,
    setPlanLoaded,
    planMode,
    setPlanMode,
    plan,
    setPlan,
    lastSavedPlan,
    setLastSavedPlan,
    updatePlanByPaymentAmount,
    paymentMethods,
    paymentFrequencies,
    hasArrangement,
    setHasArrangement,
    getFinalPaymentAmount,
    calculateStartDate,
    calculateEndDate,
    daysOfWeek,
  };

  return (
    <ArrangementContext.Provider value={contextValue}>
      {children}
    </ArrangementContext.Provider>
  );
};

export default ArrangementProvider;
